import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "~/lib/orpc-client";
import { useAuthenticatedUser } from "~/hooks/useAuthenticatedUser";
import { QUERY_KEYS } from "~/constants/queryKeys";
import ApplicationHeader from "~/components/application/shared/application-header";
import ApplicationDetails from "~/components/application/shared/application-details";
import CollaboratorsList from "~/components/application/shared/collaborators-list";
import DocumentsList from "~/components/application/shared/documents-list";
import { AiDetectionBreakdown } from "~/components/application/shared/ai-detection-breakdown";
import { Spinner } from "~/components/ui/spinner";

export const Route = createFileRoute(
  "/$tenantSlug/applications/$applicationId/",
)({
  component: TenantApplicationView,
});

function TenantApplicationView() {
  const { applicationId } = Route.useParams();
  const { isPublisherAccount, capabilities } = useAuthenticatedUser();

  const { data: application, isLoading } = useQuery({
    queryKey: QUERY_KEYS.APPLICATION_BY_ID(applicationId),
    queryFn: () =>
      apiClient.applications.getById({ id: Number(applicationId) }),
    enabled: !!applicationId,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Spinner className="size-12 text-primary" />
      </div>
    );
  }
  if (!application) return <div className="p-6">Application not found</div>;

  const isWriterApplication =
    application.category?.toUpperCase() === "WRITER" ||
    Boolean(application.writerType);
  const showCollaborators =
    application.category !== "THESIS" && !isWriterApplication;
  const showDocuments = !isWriterApplication;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto p-6">
        <ApplicationHeader
          application={application}
          isOrgMember={isPublisherAccount}
        />
        <ApplicationDetails application={application} />
        {showCollaborators && (
          <CollaboratorsList
            collaborators={application.collaborators}
            applicationId={application.id}
            applicationStatus={application.applicationStatus}
            category={application.category}
            writerType={application.writerType}
          />
        )}
        {showDocuments && (
          <DocumentsList
            uploads={application.uploads}
            category={application.category}
            writerType={application.writerType}
            contentType={application.contentType}
            thesisType={application.thesisType}
          />
        )}
        {capabilities?.canViewAiDetectionBreakdown && (
          <AiDetectionBreakdown applicationId={application.id} />
        )}
      </div>
    </div>
  );
}
